import React, { useEffect, useState, useRef } from 'react';
import './UserManager.css';
import * as api from 'api.js';
import * as pwgen from 'generate-password';
import { useGlobalState } from 'hooks';
import { Modal, Form } from 'react-bootstrap';

export const UserCreateModal = ({ show, setShow }) => {
    const [state, dispatch] = useGlobalState();

    const [validationEnabled, setValidationEnabled] = useState(false);
    const [username, setUsername] = useState('');
    const [userCompany, setUserCompany] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userPhone, setUserPhone] = useState('');
    const [formData, setFormData] = useState(null);
    const [invalidEmail, setInvalidEmail] = useState(false);
    const [invalidPhone, setInvalidPhone] = useState(false);

    const formRef = useRef(null);

    /*
    let password = pwgen.generate({
        numbers: true,
        symbols: true,
        length: 12,
        strict: true,
        exclude: "'^|£{[]}'`~ˇ§½_-;.,",
        excludeSimilarCharacters: true,
    });
    */

    useEffect(() => {
        if (!formData) return;
        dispatch.setBusy(true);
        const createUser = async () => {
            try {
                const response = await api.createUser(formData);
                dispatch.refreshUserBrowser();
                cleanUp();                
            } catch (error) {
                if (error.response) {
                    console.log(JSON.stringify(error.response));
                }
            } finally {
                dispatch.setBusy(false);
            }
        };
        createUser();
    }, [formData]);

    const cleanUp = () => {
        formRef.current.reset();
        setUsername('');
        setUserCompany('');
        setUserEmail('');
        setUserPhone('');
        setValidationEnabled(false);
        setFormData(null);
        setShow(false);
        setInvalidEmail(false);
        setInvalidPhone(false);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (!formIsValid()) {
            setValidationEnabled(true);
            return;
        }

        setFormData(
            JSON.stringify({
                username: username.trim(),
                email: userEmail.trim(),
                phone_number: userPhone.trim(),
                temporary_password: pwgen.generate({
                    numbers: true,
                    symbols: true,
                    length: 12,
                    strict: true,
                    exclude: "'^|£{[]}'`~ˇ§½_-;.,",
                    excludeSimilarCharacters: true,
                }),
                company: userCompany.trim(),
            })
        );
    };

    function formIsValid() {
        setInvalidEmail(false);
        setInvalidPhone(false);

        if (!username.length || !userCompany.length || !userEmail.length || !userPhone.length) {
            return false;
        }

        if (!emailIsValid(userEmail)) {
            setInvalidEmail(true);
            return false;
        }

        if (!phonenumberIsValid(userPhone)) {
            setInvalidPhone(true);
            return false;
        }

        return true;
    }

    return (
        <div>
            <Modal show={show}>
                <Modal.Header>Add new user</Modal.Header>
                <Modal.Body>
                    <Form ref={formRef} id="user-create-form" noValidate onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Label>Username:</Form.Label>
                            <Form.Control
                                value={username}
                                onChange={(event) => setUsername(event.target.value)}
                                maxLength="30"
                                isInvalid={validationEnabled && !username.length}
                            />
                            <Form.Control.Feedback type="invalid">
                                Add username
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>User company:</Form.Label>
                            <Form.Control
                                value={userCompany}
                                onChange={(event) => setUserCompany(event.target.value)}
                                maxLength="30"
                                isInvalid={validationEnabled && !userCompany.length}
                            />
                            <Form.Control.Feedback type="invalid">
                                Add user company
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>User email address:</Form.Label>
                            <Form.Control
                                value={userEmail}
                                onChange={(event) => {
                                    setValidationEnabled(false);
                                    setUserEmail(event.target.value);
                                }}
                                maxLength="30"
                                isInvalid={validationEnabled && (!userEmail.length || invalidEmail)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {!userEmail.length && <span>Add user email address</span>}
                                {invalidEmail && userEmail.length && (
                                    <span>Invalid email address</span>
                                )}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>User phone number:</Form.Label>
                            <Form.Control
                                value={userPhone}
                                onChange={(event) => {
                                    setValidationEnabled(false);
                                    setUserPhone(event.target.value);
                                }}
                                maxLength="30"
                                isInvalid={validationEnabled && (!userPhone.length || invalidPhone)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {!userPhone.length && <span>Add user phone number</span>}
                                {invalidPhone && userPhone.length && (
                                    <span>
                                        Invalid phone number: must be +358 prefixed, only numbers
                                        allowed.
                                    </span>
                                )}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <button className="styled_button" onClick={cleanUp}>Cancel</button>
                    <button className="styled_button" type="submit" form="user-create-form">
                        Add new user
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

function emailIsValid(email) {
    let re = /^[^\s@]+@[^\s@]+$/;
    return re.test(email);
}

function phonenumberIsValid(number) {
    let re = /^\+358\d{4,}/;
    return re.test(number);
}
